// src/scenes/users/index.jsx

import React, { useMemo, useState } from "react";
import {
  Box,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  Button,
  Tabs,
  Tab,
  Stack,
  TextField,
  Chip,
  Typography,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "components/Header";
import { useQuery, gql, useMutation } from "@apollo/client";
import AddIcon from "@mui/icons-material/Add";

import UserForm from "./form";
import UserActionsMenu from "components/UserActionsMenu";
import FineIssueDialog from "./FineIssueDialog";
import Member360Modal from "components/Member360Modal";
import { maskEmail, maskPhone } from "utils/formatters";

// ✅ Use correct clients per service
import { userClient, groupClient } from "state/apolloClient";

// -----------------------------
// Users (User service)
// -----------------------------
const GET_USERS = gql`
  query {
    users(user: {}) {
      id
      firstName
      lastName
      email
      phoneNumber
      role
      group
      isActive
      roleId
      idNumber
      address
    }
  }
`;

const CREATE_USER = gql`
  mutation CreateUser($newUser: UsersInput!) {
    createUser(newUser: $newUser) {
      data
      errorMessage
      isSuccess
    }
  }
`;

const UPDATE_USER = gql`
  mutation UpdateUser($updatedUserData: UsersInput!) {
    updateUser(updatedUserData: $updatedUserData) {
      data
      errorMessage
      isSuccess
    }
  }
`;

const RESET_PASSWORD = gql`
  mutation ResetPassword($id: Int!) {
    resetPassword(id: $id) {
      changePassword
      password
      username
    }
  }
`;

// -----------------------------
// Member Shares (Group service)
// -----------------------------
const GET_MEMBER_SHARES = gql`
  query GetMemberShares($userId: Int, $year: String) {
    memberContributionShares(userId: $userId, year: $year) {
      id
      userId
      username
      name
      shares
      year
      createdDate
      updatedDate
    }
  }
`;

const getApolloErrorMessage = (err) => {
  const gqlErr = err?.graphQLErrors?.[0];
  if (gqlErr) {
    const extMsg = gqlErr?.extensions?.message;
    if (typeof extMsg === "string" && extMsg.trim()) return extMsg;
    if (typeof gqlErr.message === "string" && gqlErr.message.trim()) return gqlErr.message;
  }
  return err?.message || "An unexpected error occurred.";
};

const getRoleGroupId = () => {
  const fromLS = localStorage.getItem("roleGroupId");
  if (fromLS) return Number(fromLS);

  const token = localStorage.getItem("token");
  if (!token) return NaN;

  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const payload = JSON.parse(json);
    const rg = payload.RoleGroupId ?? payload.roleGroupId;
    if (rg != null) {
      localStorage.setItem("roleGroupId", String(rg));
      return Number(rg);
    }
  } catch (e) {
    console.warn("Failed to decode JWT", e);
  }
  return NaN;
};

const Users = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const roleGroupId = getRoleGroupId();
  const canCreateUser = roleGroupId === 1 || roleGroupId === 2;

  // ============================
  // Tabs
  // ============================
  const [tab, setTab] = useState(0);

  // ============================
  // Users data (Tab 1)
  // ============================
  const {
    data: usersData,
    loading: usersLoading,
    refetch: refetchUsers,
    error: usersError,
  } = useQuery(GET_USERS, { client: userClient });

  const [open, setOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const [fineOpen, setFineOpen] = useState(false);
  const [fineTarget, setFineTarget] = useState(null);
  const [member360Open, setMember360Open] = useState(false);
  const [member360Target, setMember360Target] = useState(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    idNumber: "",
    address: "",
    roleId: 3,
  });

  const [successMessage, setSuccessMessage] = useState("");

  const [createUser] = useMutation(CREATE_USER, { client: userClient });
  const [updateUser] = useMutation(UPDATE_USER, { client: userClient });
  const [resetPassword, { loading: resettingPassword }] = useMutation(RESET_PASSWORD, { client: userClient });

  const handleEdit = (user) => {
    setFormData(user);
    setEditingUser(user);
    setOpen(true);
  };

  const handleIssueFine = (user) => {
    setFineTarget(user);
    setFineOpen(true);
  };

  const handleFineCreated = () => {
    setSuccessMessage("Fine issued successfully.");
  };

  const handleMember360 = (user) => {
    setMember360Target(user);
    setMember360Open(true);
  };

  const handleOpenResetPassword = (user) => {
    setResetTarget(user);
    setResetDialogOpen(true);
  };

  const handleCloseResetPassword = () => {
    setResetDialogOpen(false);
    setResetTarget(null);
  };

  const handleResetPassword = async () => {
    const selectedUserId = Number(resetTarget?.id);
    if (!Number.isFinite(selectedUserId)) {
      setErrorMessage("Unable to reset password because member details are incomplete.");
      handleCloseResetPassword();
      return;
    }

    try {
      const { data } = await resetPassword({ variables: { id: selectedUserId } });
      const result = data?.resetPassword;

      if (!result) {
        setErrorMessage("Password reset failed. Please try again.");
        return;
      }

      const usernameText = result.username ? ` for ${result.username}` : "";
      const tempPasswordText = result.password ? ` Temporary password: ${result.password}` : "";
      setSuccessMessage(
        `Password reset successfully${usernameText}. The member will be required to change it on next login.${tempPasswordText}`
      );
      handleCloseResetPassword();
    } catch (err) {
      setErrorMessage(getApolloErrorMessage(err));
    }
  };

  const handleSubmit = async () => {
    try {
      const mutation = editingUser ? updateUser : createUser;
      const cleanFormData = { ...formData };
      delete cleanFormData.role;
      delete cleanFormData.group;
      delete cleanFormData.__typename;

      const variables = editingUser
        ? { updatedUserData: { ...cleanFormData, id: editingUser.id } }
        : { newUser: cleanFormData };

      const { data: resp } = await mutation({ variables });
      const result = editingUser ? resp.updateUser : resp.createUser;

      if (result.isSuccess) {
        await refetchUsers();
        setSuccessMessage(editingUser ? "User updated successfully" : "User created successfully");
        setOpen(false);
        setEditingUser(null);
      } else {
        alert(result.errorMessage || "Operation failed");
      }
    } catch (err) {
      console.error(err);
      alert("An error occurred");
    }
  };

  const userColumns = [
    { field: "sequence", headerName: "#", width: 70, sortable: false, filterable: false },
    { field: "firstName", headerName: "First Name", flex: 1 },
    { field: "lastName", headerName: "Last Name", flex: 1 },
    {
      field: "email",
      headerName: "Email",
      flex: 1.5,
      renderCell: (params) => <span>{maskEmail(params.value)}</span>,
    },
    {
      field: "phoneNumber",
      headerName: "Phone Number",
      flex: 1,
      renderCell: (params) => <span>{maskPhone(params.value)}</span>,
    },
    { field: "role", headerName: "Role", flex: 1 },
    { field: "group", headerName: "Group", flex: 1.5 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <UserActionsMenu
          user={params.row}
          onEdit={() => handleEdit(params.row)}
            onIssueFine={() => handleIssueFine(params.row)}
            onMember360={() => handleMember360(params.row)}
            onResetPassword={() => handleOpenResetPassword(params.row)}
          />
        ),
    },
  ];

  // ============================
  // Member Shares data (Tab 2)
  // ============================
  const [sharesYear, setSharesYear] = useState(String(new Date().getFullYear()));
  const [sharesUserId, setSharesUserId] = useState("");

  const sharesVariables = useMemo(() => {
    return {
      year: sharesYear?.trim() || null,
      userId: sharesUserId ? Number(sharesUserId) : null,
    };
  }, [sharesYear, sharesUserId]);

  const {
    data: sharesData,
    loading: sharesLoading,
    error: sharesError,
    refetch: refetchShares,
  } = useQuery(GET_MEMBER_SHARES, {
    client: groupClient,
    variables: sharesVariables,
    fetchPolicy: "cache-and-network",
    // Optional: only fetch when the tab is opened
    skip: tab !== 1,
  });

  const shareRows = useMemo(
    () => sharesData?.memberContributionShares || [],
    [sharesData]
  );

  const sharesSummary = useMemo(() => {
    const totalRecords = shareRows.length;
    const totalMembers = usersData?.users?.length || 0;

    // Count unique members that have updated their shares for the selected year.
    const updatedUserIds = new Set(
      shareRows
        .filter((r) => Number(r?.shares || 0) > 0)
        .map((r) => r?.userId)
        .filter(Boolean)
    );
    const updatedCount = updatedUserIds.size;

    const pendingCount = Math.max(totalMembers - updatedCount, 0);
    const allUpdated = totalMembers > 0 && pendingCount === 0;

    return { totalRecords, totalMembers, updatedCount, pendingCount, allUpdated };
  }, [shareRows, usersData]);

  const sharesColumns = [
    { field: "sequence", headerName: "#", width: 70, sortable: false, filterable: false },
    { field: "username", headerName: "Username", flex: 1.2 },
    { field: "name", headerName: "Group", flex: 1.2 },
    {
      field: "shares",
      headerName: "Shares",
      flex: 0.7,
      valueFormatter: (p) => (p.value != null ? Number(p.value).toLocaleString() : "-"),
    },
    { field: "year", headerName: "Year", flex: 0.6 },
    {
      field: "status",
      headerName: "Shares Updated?",
      flex: 1,
      sortable: false,
      valueGetter: (params) => Number(params.row?.shares || 0) > 0,
      renderCell: (params) =>
        params.value ? (
          <Chip label="Updated" size="small" color="success" sx={{ fontWeight: 700 }} />
        ) : (
          <Chip label="Pending" size="small" color="warning" variant="outlined" sx={{ fontWeight: 700 }} />
        ),
    },
    {
      field: "updatedDate",
      headerName: "Last Updated",
      flex: 1,
      valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString() : "-"),
    },
  ];

  // ============================
  // Shared Grid Styling
  // ============================
  const gridSx = {
    "& .MuiDataGrid-root": { border: "none" },
    "& .MuiDataGrid-cell": { borderBottom: "none" },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: theme.palette.background.alt,
      color: theme.palette.secondary[100],
      borderBottom: "none",
    },
    "& .MuiDataGrid-virtualScroller": {
      backgroundColor: theme.palette.primary.light,
    },
    "& .MuiDataGrid-footerContainer": {
      backgroundColor: theme.palette.background.alt,
      color: theme.palette.secondary[100],
      borderTop: "none",
    },
  };

  return (
    <Box sx={{ m: { xs: "0.75rem", sm: "1rem 1.5rem", md: "1.5rem 2.5rem" }, minWidth: 0 }}>
      <Header title="MEMBERS" subtitle="Members and Shareholding" />

      {/* Tabs */}
      <Box sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant={isMobile ? "scrollable" : "standard"} scrollButtons="auto" allowScrollButtonsMobile>
          <Tab label="Members" />
          <Tab label="Member Shares" />
        </Tabs>
      </Box>

      {/* -------------------------
          TAB 1: MEMBERS
         ------------------------- */}
      {tab === 0 && (
        <>
          {/* Create / Edit User form */}
          <UserForm
            open={open}
            onClose={() => {
              setOpen(false);
              setEditingUser(null);
            }}
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
          />

          {/* Issue Fine dialog */}
          <FineIssueDialog
            open={fineOpen}
            onClose={() => setFineOpen(false)}
            user={fineTarget}
            onCreated={handleFineCreated}
          />

          <Member360Modal
            open={member360Open}
            onClose={() => {
              setMember360Open(false);
              setMember360Target(null);
            }}
            member={member360Target}
          />

          <Dialog
            open={resetDialogOpen}
            onClose={handleCloseResetPassword}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Reset Password</DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mt: 1 }}>
                You are about to reset this member&apos;s password. The member will be required to change their password on next login.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={handleCloseResetPassword} disabled={resettingPassword}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleResetPassword}
                disabled={resettingPassword}
              >
                {resettingPassword ? "Resetting..." : "Reset Password"}
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={!!successMessage}
            autoHideDuration={4000}
            onClose={() => setSuccessMessage("")}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert onClose={() => setSuccessMessage("")} severity="success" sx={{ width: "100%" }}>
              {successMessage}
            </Alert>
          </Snackbar>
          <Snackbar
            open={!!errorMessage}
            autoHideDuration={5000}
            onClose={() => setErrorMessage("")}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert onClose={() => setErrorMessage("")} severity="error" sx={{ width: "100%" }}>
              {errorMessage}
            </Alert>
          </Snackbar>

          {usersError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {getApolloErrorMessage(usersError)}
            </Alert>
          ) : null}

          {canCreateUser && (
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
                Create User
              </Button>
            </Box>
          )}

          <Box mt="10px" height="75vh" sx={gridSx}>
            <DataGrid
              loading={usersLoading}
              rows={(usersData?.users || []).map((row, index) => ({ ...row, sequence: index + 1 }))}
              getRowId={(row) => row.id}
              columns={userColumns}
            />
          </Box>
        </>
      )}

      {/* -------------------------
          TAB 2: MEMBER SHARES (read-only)
         ------------------------- */}
      {tab === 1 && (
        <>
          {sharesError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {getApolloErrorMessage(sharesError)}
            </Alert>
          ) : null}

          {/* Summary */}
          <Card sx={{ borderRadius: 3, mb: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={800}>
                Shareholding Status — {sharesYear}
              </Typography>

              <Divider sx={{ my: 1.5, opacity: 0.35 }} />

              <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
                <Chip label={`Members in list: ${sharesSummary.totalMembers}`} />
                <Chip label={`Updated: ${sharesSummary.updatedCount}`} />
                <Chip label={`Pending: ${sharesSummary.pendingCount}`} />

                {sharesSummary.allUpdated ? (
                  <Chip color="success" label="All members updated ✔" />
                ) : (
                  <Chip color="warning" variant="outlined" label="Some members have not updated shares" />
                )}

                <Typography variant="body2" sx={{ color: theme.palette.secondary[200] }}>
                  Confirm this before populating the contribution schedule.
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          {/* Filters */}
          <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={2} mb={2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Year"
                value={sharesYear}
                onChange={(e) => setSharesYear(e.target.value)}
                sx={{ minWidth: 140 }}
              />
              <TextField
                label="User ID (optional)"
                value={sharesUserId}
                onChange={(e) => setSharesUserId(e.target.value)}
                sx={{ minWidth: 180 }}
              />
              <Button variant="outlined" onClick={() => refetchShares()}>
                Refresh
              </Button>
            </Stack>
          </Box>

          <Box mt="10px" height="75vh" sx={gridSx}>
            <DataGrid
              loading={sharesLoading}
              rows={shareRows.map((row, index) => ({ ...row, sequence: index + 1 }))}
              getRowId={(row) => row.id}
              columns={sharesColumns}
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
              }}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default Users;
